import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "substation-automation-systems",
  kind: "domain-vertical",
  title: "Substation Automation & Distribution Systems",
  shortTitle: "Substation Automation",
  status: "draft",
  tier: "experience",
  seoTitle: "Substation Automation & Distribution Systems | GA",
  seoDescription:
    "Substation DCS/RTU modernization, protective relay integration, auxiliary DC systems, and transmission control center telemetry engineering.",
  summary:
    "Substation DCS and RTU modernizations, protective relay telemetry integration, auxiliary DC systems, and data pipelines connecting substations to transmission control centers.",
  image: "/images/blog/high-frequency-telemetry-rendering-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["energy"],
  relatedPostSlugs: [
    "high-frequency-telemetry-rendering",
    "mapping-the-undocumented-network",
    "air-gap-is-a-myth",
  ],
  engineering: [
    "Substation common modernization engineering integrating distributed control systems (DCS) and RTUs",
    "Protective relay and Intelligent Electronic Device (IED) integration over DNP3, Modbus, and IEC 61850 protocols",
    "Telemetry ingestion pipelines streaming real-time voltage, current, frequency, and breaker status to transmission control centers (EMS)",
    "Auxiliary battery system monitoring, station power feeds, DC distribution panels, and transfer switch logic",
    "Substation yard physical engineering: lighting design, grounding grids, conduit schedules, and marshalling enclosures",
    "Electronic Security Perimeter (ESP) isolation and serial-to-Ethernet gateway hardening in electrical switchyards",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "The High-Voltage Substation Is the Core Transmission Node",
    },
    {
      type: "paragraph",
      text: "Substations sit at the critical junction between bulk generation and regional power distribution. Modernizing these environments requires bridging electromechanical protective relays, modern microprocessor-based IEDs, Remote Terminal Units (RTUs), and substation DCS networks without compromising protective clearing times or introducing unmonitored communication failures into the bulk electric system.",
    },
    {
      type: "heading",
      level: 2,
      text: "Unified Telemetry from the Switchyard to the Grid Control Center",
    },
    {
      type: "paragraph",
      text: "Reliable substation automation connects switchyard instrumentation directly to transmission operations. By standardizing communication protocols, hardening telecommunication gateways, engineering redundant auxiliary DC battery monitoring, and modeling breaker states accurately for Energy Management Systems (EMS), utilities gain real-time grid observability and automated fault isolation.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit substation switchyard equipment, existing RTUs, relay protective schemes, DC battery banks, and telecommunications circuits.",
      produces: [
        "Substation equipment inventory and one-line drawing verification",
        "Protective relay, IED, and RTU communication protocol audit",
        "Auxiliary DC battery capacity and station service power assessment",
        "Telecommunications circuit reliability and latency analysis",
      ],
      fit: {
        good: [
          "Electric utilities, transmission operators, and large industrial facilities operating high-voltage substations and switchyards",
          "Organizations modernizing legacy RTUs, integrating microprocessor relays, or connecting substations to centralized EMS/SCADA",
        ],
        poor: [
          "Residential low-voltage wiring projects with no utility-scale switchgear or protective relaying requirements",
          "Pure retail commercial building facilities without high-voltage power distribution assets",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect substation automation topologies, communications networks, IED mapping tables, and control house layout designs.",
      produces: [
        "Substation automation system network architecture (DNP3, Modbus, IEC 61850)",
        "IED and protective relay point mapping schedules for EMS/SCADA integration",
        "Substation control house equipment rack and panel layout designs",
        "Switchyard physical electrical specifications including lighting and conduit runs",
      ],
    },
    "detailed-design": {
      focus:
        "Produce detailed AC/DC schematics, wiring interconnect diagrams, relay communication settings, and commissioning test plans.",
      produces: [
        "Detailed substation AC/DC elementary drawings and one-line diagrams",
        "Cable schedules, conduit layout plans, and termination schedules",
        "RTU configuration files, protocol gateway settings, and firewall rule tables",
        "Commissioning test procedures and relay point-to-point verification matrices",
      ],
    },
    implementation: {
      focus:
        "Supervise substation panel installation, execute point-to-point telemetry verification with grid control centers, and commission systems.",
      produces: [
        "Executed point-to-point checkout records verifying telemetry to the Energy Management System",
        "Commissioned substation RTU, IED gateways, and auxiliary monitoring systems",
        "As-built electrical drawing packages and relay setting sheets",
        "Substation operational runbooks and maintenance procedures",
      ],
    },
  },
};
